import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { projectsApi, filesApi, type Project, type ProjectCreateRequest } from '../lib/api'
import { useAuth } from '../contexts/IframeAuthProvider'
import { useCreateProject, useDeleteProject } from '../lib/hooks'
import { Button } from '../components/ui/Button'
import { StatusBadge } from '../components/ui/Badge'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { UploadVendorResponsesModal } from '../components/modals/UploadVendorResponsesModal'
import { ProjectSettingsModal } from '../components/modals/ProjectSettingsModal'
import toast from 'react-hot-toast'
import { 
  Plus,
  Search,
  FolderOpen,
  Trash2,
  Settings
} from 'lucide-react'
import { ProjectIcon } from '../components/ui/ProjectIcon'
import styles from '../components/ui/ProjectsScroller.module.css'

export const DashboardPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setShowUploadScope, handleDraggedFilesUpload } = useOutletContext<{ 
    setShowUploadScope: (show: boolean) => void
    handleDraggedFilesUpload: (files: File[]) => void
  }>()
  const [showUploadResponses, setShowUploadResponses] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [isDragOver, setIsDragOver] = useState(false)
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Fetch projects from API
  const { data: projectsData, isLoading, error, refetch } = useQuery(
    ['projects', { query: debouncedSearchQuery, limit: 100 }], // Increased limit for better client-side filtering
    () => projectsApi.list({ query: debouncedSearchQuery || undefined, limit: 100 }),
    {
      keepPreviousData: true,
    }
  )

  const deleteProject = useDeleteProject()

  // Handle different possible response structures
  const allProjects = Array.isArray(projectsData?.data) 
    ? projectsData.data 
    : Array.isArray((projectsData as any)?.projects)
    ? (projectsData as any).projects
    : Array.isArray(projectsData) 
    ? projectsData 
    : []

  // Client-side filtering for search (fallback in case API search isn't working)
  const projects = allProjects.filter((project: Project) => {
    if (!searchQuery.trim()) return true
    
    const query = searchQuery.toLowerCase()
    return (
      project.title.toLowerCase().includes(query) ||
      project.status.toLowerCase().includes(query) ||
      (project.due_date && project.due_date.toLowerCase().includes(query))
    )
  })

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_progress':
      case 'evaluation':
      case 'active':
      case 'draft': // Treat draft as in_progress
        return (
          <div className="inline-flex items-center px-[5px] py-[5px] rounded-sm text-xs font-normal bg-[#eaf9fa] text-[#229090]">
            <div className="w-3 h-3 mr-1">
              <svg width="12" height="13" viewBox="0 0 12 13" fill="none">
                <circle cx="6" cy="6.5" r="6" fill="#229090"/>
              </svg>
            </div>
            IN PROGRESS
          </div>
        )
      case 'completed':
      case 'closed':
        return (
          <div className="inline-flex items-center px-[5px] py-[5px] rounded-sm text-xs font-normal bg-[#e6f8bc] text-[#577b09]">
            <div className="w-3 h-3 mr-1">
              <svg width="12" height="13" viewBox="0 0 12 13" fill="none">
                <circle cx="6" cy="6.5" r="6" fill="#577b09"/>
              </svg>
            </div>
            CLOSED
          </div>
        )
      default:
        // Default to in_progress for any unknown status
        return (
          <div className="inline-flex items-center px-[5px] py-[5px] rounded-sm text-xs font-normal bg-[#eaf9fa] text-[#229090]">
            <div className="w-3 h-3 mr-1">
              <svg width="12" height="13" viewBox="0 0 12 13" fill="none">
                <circle cx="6" cy="6.5" r="6" fill="#229090"/>
              </svg>
            </div>
            IN PROGRESS
          </div>
        )
    }
  }

  const normalizeStatus = (status: string): 'in_progress' | 'closed' => {
    switch (status) {
      case 'completed':
      case 'closed':
        return 'closed'
      default:
        return 'in_progress' // Default all projects to in_progress
    }
  }

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      // Filter for PDF and DOC files
      const validFiles = files.filter(file => 
        file.type === 'application/pdf' || 
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/msword'
      )
      
      if (validFiles.length > 0) {
        handleDraggedFilesUpload(validFiles)
      } else {
        toast.error('Please upload PDF or Word documents only')
      }
    }
  }

  const handleDeleteProject = async (projectId: string, projectTitle: string) => {
    if (!window.confirm(`Are you sure you want to delete the project "${projectTitle}"? This will permanently delete the project and all its vendor responses, evaluations, and related data. This action cannot be undone.`)) {
      return
    }

    setDeletingProjectId(projectId)
    
    try {
      await deleteProject.mutateAsync(projectId)
      toast.success('Project deleted successfully')
      refetch() // Refresh the projects list
    } catch (error) {
      console.error('Error deleting project:', error)
      toast.error('Failed to delete project')
    } finally {
      setDeletingProjectId(null)
    }
  }

  const handleOpenSettings = (project: Project) => {
    setSelectedProject(project)
    setShowSettingsModal(true)
  }

  const handleUpdateProject = async (data: { title: string; due_date: string }) => {
    if (!selectedProject) return
    
    try {
      await projectsApi.update(selectedProject.id, data)
      toast.success('Project updated successfully')
      refetch() // Refresh project data
      queryClient.invalidateQueries(['projects']) // Update cache
      setShowSettingsModal(false)
    } catch (error) {
      toast.error('Failed to update project')
      throw error
    }
  }

  return (
    <>
      {/* Main Content */}
        <div className="flex-1 flex flex-col h-screen p-6 overflow-hidden">
          {/* Split Content Area */}
          <div className="flex-1 flex flex-col">
            {/* Upload Scope Documents Area - Fixed Height */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4" style={{ minHeight: '280px' }}>
              {/* Title outside the purple container */}
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Upload Scope Documents
              </h3>
              
              <div className="flex items-center justify-center">
                <div className="w-full">
                  <div 
                    className={`bg-purple-50 rounded-lg border-2 border-dashed p-6 flex items-center justify-center transition-colors ${
                      isDragOver 
                        ? 'border-purple-500 bg-purple-100' 
                        : 'border-purple-300'
                    }`}
                    style={{ minHeight: '180px' }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="text-center">
                      {/* Upload illustration */}
                      <div className="w-28 h-28 mx-auto mb-4 flex items-center justify-center">
                        <img 
                          src="/upload-illustration.svg" 
                          alt="Upload illustration" 
                          className="w-full h-full"
                        />
                      </div>
                      
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">
                        Drag and drop your documents here
                      </h4>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowUploadScope(true)}
                        className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white"
                      >
                        Browse your documents
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Projects Grid - Remaining Space */}
            <div className="flex-1 flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 p-6 min-w-0 overflow-hidden min-h-0">
              <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <h2 className="text-2xl font-bold text-gray-900">Projects</h2>
                <div className="relative flex-shrink-0">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Type to search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 w-64 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex-1 flex flex-col overflow-hidden">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <LoadingSpinner size="lg" />
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <p className="text-red-600 mb-2">Failed to load projects</p>
                      <p className="text-gray-500 text-sm">Please try again later</p>
                    </div>
                  </div>
                ) : projects.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">No projects found</p>
                      {searchQuery && (
                        <p className="text-gray-400 text-sm mt-1">Try adjusting your search</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={`${styles.projectsScroller} w-full max-w-full`}>
                    <div className="flex space-x-4 pb-4">
                      {projects.map((project: Project) => (
                        <div
                          key={project.id}
                          className="flex-shrink-0 w-80 bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer relative"
                          onClick={() => navigate(`/projects/${project.id}`)}
                        >
                          {/* Settings and Delete Buttons */}
                          <div className="absolute top-4 right-4 flex gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleOpenSettings(project)
                              }}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                              title="Project settings"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteProject(project.id, project.title)
                              }}
                              disabled={deletingProjectId === project.id}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Delete project"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Status Badge */}
                          <div className="mb-6">
                            {getStatusBadge(project.status)}
                          </div>

                          {/* Project Icon and Title */}
                          <div className="flex items-start space-x-4 mb-8">
                            <ProjectIcon 
                              status={normalizeStatus(project.status)} 
                              className="flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0 pr-8">
                              <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
                                {project.title}
                              </h3>
                            </div>
                          </div>

                          {/* Project Stats */}
                          <div className="grid grid-cols-2 gap-2">
                            <div className="text-center">
                              <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2 whitespace-nowrap">
                                BID #
                              </p>
                              <p className="text-sm font-bold text-gray-900">
                                {project.project_number || 'N/A'}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2 whitespace-nowrap">
                                VENDORS
                              </p>
                              <p className="text-sm font-bold text-gray-900">
                                {project.vendor_count || 0}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2 whitespace-nowrap">
                                PUBLISHED DATE
                              </p>
                              <p className="text-sm font-bold text-gray-900">
                                {project.created_at ? 
                                  new Date(project.created_at).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: '2-digit', 
                                    year: 'numeric'
                                  }).replace(/\//g, '/') 
                                  : 'N/A'
                                }
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2 whitespace-nowrap">
                                DUE DATE
                              </p>
                              <p className="text-sm font-bold text-gray-900">
                                {project.due_date ? 
                                  new Date(project.due_date).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: '2-digit', 
                                    year: 'numeric'
                                  }).replace(/\//g, '/') 
                                  : 'N/A'
                                }
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      {/* Upload Vendor Responses Modal */}
      <UploadVendorResponsesModal
        isOpen={showUploadResponses}
        onClose={() => setShowUploadResponses(false)}
        onSubmit={(responses) => {
          console.log('Vendor responses uploaded:', responses)
          setShowUploadResponses(false)
        }}
      />

      {/* Project Settings Modal */}
      {selectedProject && (
        <ProjectSettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          onSave={handleUpdateProject}
          project={selectedProject}
        />
      )}
    </>
  )
}